import { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'

import { cn } from '@/lib/utils'

const HOME_BANNER_ASSETS = {
	logo: '/images/logo.png',
	tier: '/images/home/becy-tier.png',
	model: '/images/home/becy-model.png',
	decorLeft: '/images/home/decor_left.png',
	decorRight: '/images/home/decor_right.png',
} as const

const SLIDE_COUNT = 2

function RewardsBanner() {
	return (
		<div
			className={
				'relative flex h-[240px] w-full items-center overflow-hidden rounded-2xl border border-border bg-background px-6 py-8 sm:h-[280px] sm:px-10 sm:py-10 lg:h-[300px]'
			}
		>
			<div
				className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_125%_at_5%_-10%,hsl(var(--primary)/0.75)_0%,hsl(var(--primary)/0.35)_28%,transparent_58%),radial-gradient(ellipse_80%_95%_at_88%_50%,hsl(var(--primary)/0.7)_0%,hsl(var(--primary)/0.25)_40%,transparent_68%),radial-gradient(ellipse_50%_60%_at_100%_0%,hsl(var(--primary)/0.35)_0%,transparent_55%)]'
				aria-hidden
			/>
			<div className='pointer-events-none absolute -left-16 -top-20 h-72 w-72 rounded-full bg-primary/50 blur-[80px]' aria-hidden />
			<div className='pointer-events-none absolute -right-10 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-primary/45 blur-[90px]' aria-hidden />

			<img
				src={HOME_BANNER_ASSETS.decorLeft}
				alt=''
				className='pointer-events-none absolute inset-y-0 left-0 z-[1] h-full w-[55%] object-cover object-left opacity-90'
				draggable={false}
			/>
			<img
				src={HOME_BANNER_ASSETS.decorRight}
				alt=''
				className='pointer-events-none absolute inset-y-0 right-0 z-[1] h-full w-[70%] object-cover object-right opacity-90'
				draggable={false}
			/>
			<img
				src={HOME_BANNER_ASSETS.tier}
				alt=''
				className='pointer-events-none absolute -right-16 top-1/2 z-[2] hidden h-[320px] w-[320px] -translate-y-1/2 object-contain drop-shadow-[0_0_48px_hsl(var(--primary)/0.45)] sm:-right-20 sm:block sm:h-[400px] sm:w-[400px] md:-right-16 md:h-[440px] md:w-[440px] lg:-right-10 lg:h-[480px] lg:w-[480px]'
				draggable={false}
			/>

			<div className='relative z-10 flex max-w-[560px] flex-col gap-3 sm:gap-4'>
				<img src={HOME_BANNER_ASSETS.logo} alt='Betscycle' className='h-7 w-auto object-contain object-left sm:h-8' />
				<div className='flex flex-col gap-2'>
					<h2 className='text-3xl font-bold leading-[1.15] tracking-[-0.2px] text-foreground sm:text-4xl lg:text-[42px]'>
						Real Platform Activity.
						<br />
						Real Reward Potential.
					</h2>
					<p className='max-w-[480px] text-sm leading-[132%] tracking-[-0.21px] text-foreground/80 sm:text-base'>
						A defined share of platform-generated value is directed into the Betscycle rewards structure for eligible stakers.
					</p>
				</div>
			</div>
		</div>
	)
}

function DepositBonusBanner() {
	return (
		<div
			className={
				'relative flex h-[240px] w-full items-center overflow-hidden rounded-2xl border border-border bg-gradient-to-r from-background via-[hsl(var(--accent-pink)/0.25)] to-[hsl(var(--accent-pink)/0.75)] px-6 py-8 sm:h-[280px] sm:px-10 sm:py-10 lg:h-[300px]'
			}
		>
			<div className='pointer-events-none absolute -right-16 top-1/2 h-[320px] w-[320px] -translate-y-1/2 opacity-95 sm:-right-20 sm:h-[400px] sm:w-[400px] md:-right-16 md:h-[440px] md:w-[440px] lg:-right-10 lg:h-[480px] lg:w-[480px]'>
				<img src={HOME_BANNER_ASSETS.model} alt='' className='h-full w-full object-contain' draggable={false} />
			</div>
			<div className='pointer-events-none absolute inset-0 bg-gradient-to-r from-background from-30% via-background/70 via-50% to-transparent' />

			<div className='relative z-10 flex max-w-[55%] flex-col gap-3 sm:gap-4'>
				<img src={HOME_BANNER_ASSETS.logo} alt='Betscycle' className='h-7 w-auto object-contain object-left sm:h-8' />
				<div className='flex flex-col gap-1'>
					<span className='text-sm italic tracking-[-0.21px] text-muted-foreground'>Deposit bonus</span>
					<span className='text-4xl font-bold leading-none tracking-[-0.2px] text-foreground sm:text-5xl lg:text-6xl'>200%</span>
					<span className='text-2xl font-semibold tracking-[-0.16px] text-foreground sm:text-3xl'>up to $1000</span>
				</div>
				<p className='max-w-[420px] text-sm leading-[132%] tracking-[-0.21px] text-foreground/80'>
					Kick off your next session with a boosted first deposit — more play, more chances to climb the rewards ladder.
				</p>
			</div>
		</div>
	)
}

export function PromoBanners() {
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 25 })
	const [selectedIndex, setSelectedIndex] = useState(0)

	const onSelect = useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
	}, [emblaApi])

	useEffect(() => {
		if (!emblaApi) return
		onSelect()
		emblaApi.on('select', onSelect)
		return () => {
			emblaApi.off('select', onSelect)
		}
	}, [emblaApi, onSelect])

	useEffect(() => {
		if (!emblaApi) return
		const id = window.setInterval(() => emblaApi.scrollNext(), 6000)
		return () => window.clearInterval(id)
	}, [emblaApi])

	return (
		<section className='relative'>
			<div ref={emblaRef} className='overflow-hidden'>
				<div className='flex'>
					<div className='min-w-0 shrink-0 grow-0 basis-full'>
						<RewardsBanner />
					</div>
					<div className='min-w-0 shrink-0 grow-0 basis-full'>
						<DepositBonusBanner />
					</div>
				</div>
			</div>

			<div className='absolute bottom-4 right-5 z-10 flex items-center gap-1.5 sm:bottom-5 sm:right-6'>
				{Array.from({ length: SLIDE_COUNT }, (_, index) => (
					<button
						key={index}
						type='button'
						aria-label={`Go to banner ${index + 1}`}
						aria-current={selectedIndex === index}
						onClick={() => emblaApi?.scrollTo(index)}
						className={cn('h-1 rounded-full transition-all duration-300', selectedIndex === index ? 'w-8 bg-primary' : 'w-2 bg-foreground/30 hover:bg-foreground/50')}
					/>
				))}
			</div>
		</section>
	)
}
